import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  HStack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { isInfoPotentialClawback } from "features/financial-reporting/utils";
import { useEndOfPeriodReportRetrieveSuspense } from "gen/endpoints/end-of-period-report/end-of-period-report";
import {
  getFundHypotheticalWaterfallCarryClawbackRetrieveQueryKey,
  useFundHypotheticalWaterfallCarryClawbackRetrieveSuspense,
  useHypotheticalWaterfallProtectedClawbackCreate,
  useHypotheticalWaterfallRetrieveSuspense,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundListSuspense } from "gen/endpoints/fund-entities/fund-entities";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import type { FundHypotheticalProtectedClawbackInfo } from "gen/model/fundHypotheticalProtectedClawbackInfo";
import { HypotheticalWaterfallProtectedClawbackAPICreateDataRequestFundIdToProtectedCarryAmount } from "gen/model/hypotheticalWaterfallProtectedClawbackAPICreateDataRequestFundIdToProtectedCarryAmount";
import { range } from "lodash-es";
import { useMemo } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  ContentBlock,
  ContentBlockBadges,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormUnsavedIndicator } from "shared/components/form/FormUnsavedIndicator";
import { FormMoneyInput } from "shared/components/input/text/MoneyInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { ErrorIcon } from "shared/icons/ErrorIcon";
import { rootRoute } from "shared/navigation/Routes";
import { decimalOrZero } from "shared/utils/DecimalUtils";
import { formatMoney } from "shared/utils/MoneyUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const HypotheticalWaterfallDesignateProtectedCarry: React.FC = () => {
  const { t } = useTranslation(["financialReporting", "common"]);
  const queryClient = useQueryClient();

  const { fundFamilyId, endOfPeriodReportId, hypotheticalWaterfallId } =
    useRequiredStringParams();

  const navigate = useNavigate();

  const { data: clawbackData } =
    useFundHypotheticalWaterfallCarryClawbackRetrieveSuspense(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
    );

  const { data: hypotheticalWaterfall } =
    useHypotheticalWaterfallRetrieveSuspense(
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
    );

  const { data: endOfPeriodReportData } = useEndOfPeriodReportRetrieveSuspense(
    fundFamilyId,
    endOfPeriodReportId,
  );

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    endOfPeriodReportData.fund_family_event_id,
  );

  const { mutateAsync: persistProtectedCarryBack } =
    useHypotheticalWaterfallProtectedClawbackCreate();

  const fundIds = Object.keys(
    clawbackData.fund_id_to_hypothetical_clawback_info,
  );

  const { data: fundListData } = useFundListSuspense({
    fund_ids: fundIds,
  });

  const funds = useMemo(
    () => fundIds.map((id) => fundListData.find((f) => f.id === id)!),
    [fundListData, fundIds],
  );

  const hasPotentialClawback = Object.values(
    clawbackData.fund_id_to_hypothetical_clawback_info,
  ).some((info) => isInfoPotentialClawback(info));

  const isDraftEvent =
    fundFamilyEventData.state === FundFamilyEventStateMachineFieldsEnum.draft;

  const form = useForm({
    defaultValues: Object.entries(
      clawbackData.fund_id_to_hypothetical_clawback_info,
    ).reduce(
      (result, [fundId, infoObj]) => ({
        ...result,
        [fundId]: infoObj.protected_carry,
      }),
      {},
    ),
  });

  const pathToHypo = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .financialReporting({})
    .hypotheticalWaterfall({
      hypotheticalWaterfallId: hypotheticalWaterfall.id,
      endOfPeriodReportId: hypotheticalWaterfall.end_of_period_report_id,
    })
    .review({}).$;

  const navigateToHypoLandingPage = () => {
    navigate(pathToHypo);
  };

  const onSubmit = async (
    data: HypotheticalWaterfallProtectedClawbackAPICreateDataRequestFundIdToProtectedCarryAmount,
  ) => {
    await persistProtectedCarryBack({
      fundFamilyId,
      endOfPeriodReportId,
      hypotheticalWaterfallId,
      data: {
        fund_id_to_protected_carry_amount: Object.entries(data).reduce(
          (result, [fundId, value]) => ({ ...result, [fundId]: value || "0" }),
          {},
        ),
      },
    });
    await invalidateQueryKeys(
      queryClient,
      getFundHypotheticalWaterfallCarryClawbackRetrieveQueryKey(
        fundFamilyId,
        endOfPeriodReportId,
        hypotheticalWaterfallId,
      ),
    );
  };

  return (
    <PageContent>
      <PageHeader
        title={t("financialReporting:hypoProtectedCarry.title")}
        size="lg"
        badge={{
          color: "default",
          text: clawbackData.rich_date,
        }}
        backButton={
          !isDraftEvent
            ? {
                title: t("financialReporting:hypotheticalWaterfall"),
                path: pathToHypo,
              }
            : undefined
        }
      />
      <PageBody>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={navigateToHypoLandingPage}
        >
          {hasPotentialClawback && isDraftEvent && (
            <Alert status="error" mb="16px">
              <AlertIcon>
                <ErrorIcon />
              </AlertIcon>
              <AlertDescription>
                {t(
                  "financialReporting:hypoProtectedCarry.potentialClawback.alertDescription",
                )}
              </AlertDescription>
            </Alert>
          )}
          <ContentBlockGroup defaultIndex={range(fundListData.length)}>
            {funds.map((fund) => (
              <ClawbackInfo
                key={fund.id}
                fund={fund}
                info={
                  clawbackData.fund_id_to_hypothetical_clawback_info[fund.id]
                }
                isDisabled={!isDraftEvent}
              />
            ))}
          </ContentBlockGroup>
          {isDraftEvent && (
            <FormFooter>
              <FormUnsavedIndicator />
              <FormFooterActions>
                <FormCancelButton onClick={navigateToHypoLandingPage}>
                  {t("common:exit")}
                </FormCancelButton>
                <FormSubmitButton>
                  {t("financialReporting:hypoProtectedCarry.submit")}
                </FormSubmitButton>
              </FormFooterActions>
            </FormFooter>
          )}
        </Form>
      </PageBody>
    </PageContent>
  );
};

const ClawbackInfo: React.FC<{
  fund: FundEntityReadData;
  info: FundHypotheticalProtectedClawbackInfo;
  isDisabled: boolean;
}> = ({ fund, info, isDisabled }) => {
  const { t } = useTranslation(["financialReporting"]);

  const form = useFormContext();

  const protectedCarryAmount = useWatch({
    control: form.control,
    name: fund.id,
    defaultValue: info.protected_carry,
  });

  const amountRemaining = Decimal.max(
    decimalOrZero(info.amount_subject_to_clawback).sub(
      decimalOrZero(protectedCarryAmount),
    ),
    decimalOrZero(null),
  ).toString();

  return (
    <ContentBlock>
      <ContentBlockHeader>
        <ContentBlockTitle>{fund.legal_name}</ContentBlockTitle>
        {isInfoPotentialClawback(info) && (
          <ContentBlockBadges>
            <Badge colorScheme="error">
              {t(
                "financialReporting:hypoProtectedCarry.potentialClawback.badge",
              )}
            </Badge>
          </ContentBlockBadges>
        )}
      </ContentBlockHeader>
      <ContentBlockBody pb="24px">
        <HStack alignItems="flex-start" spacing="56px">
          <VStack
            spacing="8px"
            py="8px"
            alignItems="stretch"
            flexGrow={1}
            flexBasis="50%"
          >
            <Text textStyle="overline" color="gray.600">
              {t("financialReporting:hypoProtectedCarry.carrySummary.title")}
            </Text>
            <Text textStyle="small" color="gray.600">
              {t(
                "financialReporting:hypoProtectedCarry.carrySummary.description",
              )}
            </Text>
            <VStack spacing="16px" alignItems="stretch">
              <TableContainer borderWidth="1px" borderRadius="8px">
                <Table variant="stats" size="xs">
                  <Tbody>
                    <Tr>
                      <Th>
                        {t(
                          "financialReporting:hypoProtectedCarry.carrySummary.itdCarryPaidRealized",
                        )}
                      </Th>
                      <Td>{formatMoney(info.itd_carry_paid)}</Td>
                    </Tr>
                    <Tr>
                      <Th>
                        {t(
                          "financialReporting:hypoProtectedCarry.carrySummary.itdCarryUnrealized",
                        )}
                      </Th>
                      <Td>{formatMoney(info.itd_carry_unrealized)}</Td>
                    </Tr>
                    <Tr>
                      <Th>
                        {t(
                          "financialReporting:hypoProtectedCarry.carrySummary.itdCarryRealizedAndUnrealized",
                        )}
                      </Th>
                      <Td>
                        {formatMoney(
                          (
                            Number(info.itd_carry_paid) +
                            Number(info.itd_carry_unrealized)
                          ).toString(),
                        )}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
              <TableContainer borderWidth="1px" borderRadius="8px">
                <Table variant="stats" size="xs">
                  <Tbody>
                    <Tr>
                      <Th>
                        {t(
                          "financialReporting:hypoProtectedCarry.carrySummary.itdGpTaxDistributionPaid",
                        )}
                      </Th>
                      <Td>{formatMoney(info.itd_gp_tax_distribution_paid)}</Td>
                    </Tr>
                    <Tr>
                      <Th>
                        {t(
                          "financialReporting:hypoProtectedCarry.carrySummary.itdGpTaxDistributionRepaid",
                        )}
                      </Th>
                      <Td>
                        {formatMoney(info.itd_gp_tax_distribution_repaid)}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            </VStack>
          </VStack>
          <VStack
            spacing="0"
            py="8px"
            alignItems="flex-start"
            flexGrow={1}
            flexBasis="50%"
          >
            <VStack spacing="8px" alignItems="flex-start" minW="456px">
              <Text textStyle="overline" color="gray.600">
                {t(
                  "financialReporting:hypoProtectedCarry.inputProtectedCarryAmount.title",
                )}
              </Text>
              <Text textStyle="small" color="gray.600">
                {t(
                  "financialReporting:hypoProtectedCarry.inputProtectedCarryAmount.description",
                )}
              </Text>
              <FormMoneyInput
                defaultValue={info.protected_carry}
                disabled={isDisabled}
                name={fund.id}
                unsetWhenEmpty={false}
                title={t(
                  "financialReporting:hypoProtectedCarry.inputProtectedCarryAmount.inputTitle",
                )}
                rightAddon={
                  <Text textStyle="small" color="gray.400">
                    / {formatMoney(info.itd_carry_paid)}
                  </Text>
                }
              />
            </VStack>
            <StatGroup gap="32px">
              <Stat size="xxs">
                <StatNumber>{formatMoney(info.itd_carry_paid)}</StatNumber>
                <StatLabel>
                  {t(
                    "financialReporting:hypoProtectedCarry.stats.totalCarryPaid",
                  )}
                </StatLabel>
              </Stat>
              <Stat size="xxs">
                <StatNumber>
                  {formatMoney(info.amount_subject_to_clawback)}
                </StatNumber>
                <StatLabel>
                  {t(
                    "financialReporting:hypoProtectedCarry.stats.maximumClawbackAmount",
                  )}
                </StatLabel>
              </Stat>
              <Stat size="xxs">
                <StatNumber>{formatMoney(amountRemaining)}</StatNumber>
                <StatLabel>
                  {t(
                    "financialReporting:hypoProtectedCarry.stats.amountSubjectToClawback",
                  )}
                </StatLabel>
              </Stat>
            </StatGroup>
          </VStack>
        </HStack>
      </ContentBlockBody>
    </ContentBlock>
  );
};
